// ══════════════════════════════════════════════════════════════════════════════
// OnboardingPage.js — First-time user onboarding (3 screens)
// Shows only once. Skipped if user already has an active journey.
// ══════════════════════════════════════════════════════════════════════════════
import { useState } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

const JOURNEYS = [
  { id: "career_stress",     icon: "⚡", title: "Career Stress",     sub: "Purpose, duty, and performance anxiety" },
  { id: "relationship_pain", icon: "💔", title: "Relationship Pain", sub: "Love, attachment, and letting go" },
  { id: "fear_anxiety",      icon: "🌊", title: "Fear & Anxiety",    sub: "Courage, faith, and the unknown" },
  { id: "grief_loss",        icon: "🕯️", title: "Grief & Loss",      sub: "Impermanence and moving forward" },
  { id: "anger",             icon: "🔥", title: "Anger",             sub: "Self-control, ego, and response" },
  { id: "finding_purpose",   icon: "🌟", title: "Finding Purpose",   sub: "Dharma, identity, and meaning" },
];

// ── Step indicator ────────────────────────────────────────────────────────────
function Steps({ current, total }) {
  return (
    <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginBottom: "40px" }}>
      {Array.from({ length: total }, (_, i) => (
        <div key={i} style={{
          width: i === current ? "24px" : "8px",
          height: "8px", borderRadius: "4px", transition: "all 0.3s",
          background: i === current ? "var(--saffron)" : i < current ? "rgba(232,131,42,0.4)" : "var(--bg3)",
        }} />
      ))}
    </div>
  );
}

// ── Screen 1: Welcome ─────────────────────────────────────────────────────────
function WelcomeScreen({ onNext }) {
  return (
    <div style={{ textAlign: "center", maxWidth: "520px", margin: "0 auto" }}>
      <div style={{
        fontFamily: "'Noto Sans Devanagari',serif",
        fontSize: "4.5rem", color: "var(--saffron)",
        filter: "drop-shadow(0 0 24px rgba(232,131,42,0.5))",
        marginBottom: "24px", lineHeight: 1,
        animation: "pulse-om 3s ease-in-out infinite",
      }}>ॐ</div>

      <div style={{
        fontFamily: "'Cinzel Decorative',serif",
        fontSize: "1.8rem", color: "var(--cream)",
        marginBottom: "8px", lineHeight: "1.3",
      }}>
        VedSanga
      </div>
      <div style={{
        fontSize: "1.1rem", color: "var(--text-dim)",
        fontStyle: "italic", marginBottom: "40px",
      }}>
        Not a chatbot. Not a verse lookup.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "44px", textAlign: "left" }}>
        {[
          { icon: "📖", title: "One verse a day", body: "Chosen for your specific struggle — not random, not generic." },
          { icon: "🕯️", title: "A companion with memory", body: "Remembers every day of your journey. Knows your arc. Speaks from it." },
          { icon: "📈", title: "A transformation, not an answer", body: "21 days. Your emotions tracked. A report that shows what shifted." },
        ].map(item => (
          <div key={item.icon} style={{
            display: "flex", gap: "16px", alignItems: "flex-start",
            background: "linear-gradient(135deg,var(--bg2),var(--bg3))",
            border: "1px solid var(--border)", borderRadius: "12px",
            padding: "18px 20px",
          }}>
            <div style={{ fontSize: "1.5rem", flexShrink: 0 }}>{item.icon}</div>
            <div>
              <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: "0.9rem", color: "var(--saffron)", marginBottom: "4px" }}>
                {item.title}
              </div>
              <div style={{ fontSize: "1rem", color: "var(--text)", lineHeight: "1.6" }}>
                {item.body}
              </div>
            </div>
          </div>
        ))}
      </div>

      <button onClick={onNext} style={{
        width: "100%", padding: "14px", borderRadius: "10px",
        background: "linear-gradient(135deg,var(--saffron),var(--gold))",
        border: "none", color: "white", fontSize: "1.1rem",
        fontFamily: "'Cinzel Decorative',serif", cursor: "pointer",
        letterSpacing: "0.05em", transition: "opacity 0.2s",
      }}
        onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
        onMouseLeave={e => e.currentTarget.style.opacity = "1"}
      >
        Begin my journey →
      </button>
    </div>
  );
}

// ── Screen 2: Name ────────────────────────────────────────────────────────────
function NameScreen({ onNext }) {
  const [name, setName] = useState("");

  return (
    <div style={{ textAlign: "center", maxWidth: "440px", margin: "0 auto" }}>
      <div style={{ fontSize: "2.5rem", marginBottom: "20px" }}>🙏</div>
      <div style={{
        fontFamily: "'Cinzel Decorative',serif",
        fontSize: "1.4rem", color: "var(--cream)", marginBottom: "10px",
      }}>
        What should your companion call you?
      </div>
      <div style={{ fontSize: "1rem", color: "var(--text-dim)", fontStyle: "italic", marginBottom: "36px", lineHeight: "1.7" }}>
        Your companion will remember your name across all 21 days.
        It changes how it speaks to you.
      </div>

      <input
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === "Enter" && onNext(name.trim())}
        placeholder="Your name..."
        autoFocus
        style={{
          width: "100%", padding: "14px 18px", borderRadius: "10px",
          background: "var(--bg3)", border: "1px solid var(--border)",
          color: "var(--cream)", fontSize: "1.1rem",
          fontFamily: "'Crimson Pro',serif", outline: "none",
          marginBottom: "16px", textAlign: "center",
          transition: "border-color 0.2s",
        }}
        onFocus={e => e.target.style.borderColor = "var(--saffron)"}
        onBlur={e => e.target.style.borderColor = "var(--border)"}
      />

      <button onClick={() => onNext(name.trim())} style={{
        width: "100%", padding: "14px", borderRadius: "10px",
        background: name.trim()
          ? "linear-gradient(135deg,var(--saffron),var(--gold))"
          : "var(--bg3)",
        border: name.trim() ? "none" : "1px solid var(--border)",
        color: name.trim() ? "white" : "var(--text-dim)",
        fontSize: "1rem", fontFamily: "'Cinzel Decorative',serif",
        cursor: "pointer", transition: "all 0.2s", letterSpacing: "0.05em",
      }}>
        {name.trim() ? `Continue as ${name.trim()} →` : "Skip for now →"}
      </button>
    </div>
  );
}

// ── Screen 3: Birth details ───────────────────────────────────────────────────
function BirthDetailsScreen({ user, onNext }) {
  const [dob,      setDob]      = useState("");
  const [city,     setCity]     = useState("");
  const [saving,   setSaving]   = useState(false);

  const handleNext = async () => {
    setSaving(true);
    if (dob) {
      try {
        await fetch(`${API}/user/birthdate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: user.id, dob, birth_city: city.trim() }),
        });
      } catch { /* non-critical */ }
    }
    try {
      // Skipping birth details is treated as "not interested in astrology" —
      // keeps the home page Gita-only for these users without a separate question.
      await fetch(
        `${API}/user/content-focus?user_id=${encodeURIComponent(user.id)}&focus=${dob ? "both" : "gita"}`,
        { method: "POST" }
      );
    } catch { /* non-critical */ }
    setSaving(false);
    onNext();
  };

  return (
    <div style={{ textAlign: "center", maxWidth: "460px", margin: "0 auto" }}>
      <div style={{ fontSize: "2.5rem", marginBottom: "20px" }}>🌟</div>
      <div style={{
        fontFamily: "'Cinzel Decorative',serif",
        fontSize: "1.3rem", color: "var(--cream)", marginBottom: "10px",
      }}>
        When were you born?
      </div>
      <div style={{ fontSize: "1rem", color: "var(--text-dim)", fontStyle: "italic", marginBottom: "10px", lineHeight: "1.7" }}>
        Used to calculate your personal Vedic panchang, nakshatra, and — soon — your Jyotish chart.
      </div>
      <div style={{ fontSize: "0.85rem", color: "var(--text-dim)", marginBottom: "32px" }}>
        You can skip this — it can be added in settings later.
      </div>

      <div style={{ textAlign: "left", marginBottom: "18px" }}>
        <div style={{ fontSize: "0.85rem", color: "var(--saffron)", marginBottom: "8px" }}>Date of birth</div>
        <input
          type="date"
          value={dob}
          onChange={e => setDob(e.target.value)}
          max={new Date().toISOString().split("T")[0]}
          style={{
            width: "100%", padding: "12px 16px", borderRadius: "10px",
            background: "var(--bg3)", border: `1px solid ${dob ? "var(--saffron)" : "var(--border)"}`,
            color: dob ? "var(--cream)" : "var(--text-dim)", fontSize: "1rem",
            fontFamily: "'Crimson Pro',serif", outline: "none",
            transition: "border-color 0.2s", boxSizing: "border-box",
          }}
        />
      </div>

      <div style={{ textAlign: "left", marginBottom: "28px" }}>
        <div style={{ fontSize: "0.85rem", color: "var(--saffron)", marginBottom: "8px" }}>Birth city <span style={{ color: "var(--text-dim)" }}>(optional)</span></div>
        <input
          type="text"
          value={city}
          onChange={e => setCity(e.target.value)}
          placeholder="e.g. Hyderabad, Vijayawada..."
          style={{
            width: "100%", padding: "12px 16px", borderRadius: "10px",
            background: "var(--bg3)", border: "1px solid var(--border)",
            color: "var(--cream)", fontSize: "1rem",
            fontFamily: "'Crimson Pro',serif", outline: "none",
            transition: "border-color 0.2s", boxSizing: "border-box",
          }}
          onFocus={e => e.target.style.borderColor = "var(--saffron)"}
          onBlur={e => e.target.style.borderColor = "var(--border)"}
        />
      </div>

      <button onClick={handleNext} disabled={saving} style={{
        width: "100%", padding: "14px", borderRadius: "10px",
        background: dob
          ? "linear-gradient(135deg,var(--saffron),var(--gold))"
          : "var(--bg3)",
        border: dob ? "none" : "1px solid var(--border)",
        color: dob ? "white" : "var(--text-dim)",
        fontSize: "1rem", fontFamily: "'Cinzel Decorative',serif",
        cursor: "pointer", transition: "all 0.2s", letterSpacing: "0.05em",
      }}>
        {saving ? "Saving..." : dob ? "Continue →" : "Skip for now →"}
      </button>
    </div>
  );
}

// ── Screen 4: Choose journey ──────────────────────────────────────────────────
function ChooseJourneyScreen({ user, onComplete }) {
  const [starting,  setStarting]  = useState(null);
  const [confirmed, setConfirmed] = useState(null); // journey that was just started

  const handleStart = async (j) => {
    setStarting(j.id);
    try {
      await fetch(`${API}/journey/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, journey_type: j.id }),
      });
      setConfirmed(j);
    } catch {
      setStarting(null);
    }
  };

  // Warm confirmation screen after selection
  if (confirmed) {
    return (
      <div style={{ textAlign: "center", maxWidth: "480px", margin: "0 auto" }}>
        <div style={{ fontSize: "3.5rem", marginBottom: "20px" }}>{confirmed.icon}</div>
        <div style={{
          fontFamily: "'Cinzel Decorative',serif",
          fontSize: "1.3rem", color: "var(--cream)", marginBottom: "12px",
        }}>
          Your journey begins today
        </div>
        <div style={{
          fontSize: "1.05rem", color: "var(--text-dim)", fontStyle: "italic",
          lineHeight: "1.8", marginBottom: "12px",
        }}>
          21 days. One verse at a time.
        </div>
        <div style={{
          background: "linear-gradient(135deg,rgba(232,131,42,0.1),rgba(201,149,58,0.06))",
          border: "1px solid rgba(232,131,42,0.3)", borderRadius: "12px",
          padding: "20px 24px", marginBottom: "36px",
          fontFamily: "'Crimson Pro',serif", fontSize: "1.1rem",
          color: "var(--text)", lineHeight: "1.8",
        }}>
          Your companion already knows you've chosen <strong style={{ color: "var(--saffron)" }}>{confirmed.title}</strong>.
          Every verse, every conversation from here will be shaped around what you're carrying.
        </div>
        <button onClick={onComplete} style={{
          width: "100%", padding: "14px", borderRadius: "10px",
          background: "linear-gradient(135deg,var(--saffron),var(--gold))",
          border: "none", color: "white", fontSize: "1.05rem",
          fontFamily: "'Cinzel Decorative',serif", cursor: "pointer",
          letterSpacing: "0.05em",
        }}>
          Enter VedSanga →
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "600px", margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <div style={{ fontSize: "1.8rem", marginBottom: "12px" }}>🌟</div>
        <div style={{
          fontFamily: "'Cinzel Decorative',serif",
          fontSize: "1.3rem", color: "var(--cream)", marginBottom: "8px",
        }}>
          What are you carrying right now?
        </div>
        <div style={{ fontSize: "1rem", color: "var(--text-dim)", fontStyle: "italic" }}>
          Choose the struggle closest to your heart. The Gita will meet you there.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        {JOURNEYS.map(j => (
          <div key={j.id}
            onClick={() => !starting && handleStart(j)}
            style={{
              background: "linear-gradient(135deg,var(--bg2),var(--bg3))",
              border: starting === j.id ? "1px solid var(--saffron)" : "1px solid var(--border)",
              borderRadius: "12px", padding: "20px",
              cursor: starting ? "default" : "pointer",
              transition: "all 0.2s", opacity: starting && starting !== j.id ? 0.5 : 1,
            }}
            onMouseEnter={e => { if (!starting) { e.currentTarget.style.borderColor = "var(--saffron)"; e.currentTarget.style.background = "linear-gradient(135deg,var(--bg2),rgba(232,131,42,0.06))"; } }}
            onMouseLeave={e => { if (!starting) { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "linear-gradient(135deg,var(--bg2),var(--bg3))"; } }}
          >
            <div style={{ fontSize: "1.8rem", marginBottom: "10px" }}>{j.icon}</div>
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: "0.9rem", color: "var(--saffron)", marginBottom: "4px" }}>
              {starting === j.id ? "Starting..." : j.title}
            </div>
            <div style={{ fontSize: "0.92rem", color: "var(--text-dim)", lineHeight: "1.5" }}>
              {j.sub}
            </div>
          </div>
        ))}
      </div>

      {/* Skip option */}
      <div style={{ textAlign: "center", marginTop: "24px" }}>
        <button onClick={onComplete} style={{
          background: "none", border: "none", cursor: "pointer",
          color: "var(--text-dim)", fontSize: "0.9rem", fontStyle: "italic",
          textDecoration: "underline", padding: "8px",
        }}>
          I'll explore on my own first
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN OnboardingPage
// ══════════════════════════════════════════════════════════════════════════════
export default function OnboardingPage({ user, onComplete }) {
  const [step, setStep] = useState(0);

  const saveNameAndProceed = async (name) => {
    if (name) {
      try {
        await fetch(
          `${API}/user/name?user_id=${encodeURIComponent(user.id)}&name=${encodeURIComponent(name)}`,
          { method: "POST" }
        );
      } catch { /* non-critical */ }
    }
    setStep(2);
  };

  return (
    <div style={{
      minHeight: "100vh", background: "var(--bg)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "40px 24px",
    }}>
      <div style={{ width: "100%", maxWidth: "640px" }}>
        <Steps current={step} total={4} />

        {step === 0 && <WelcomeScreen onNext={() => setStep(1)} />}
        {step === 1 && <NameScreen onNext={saveNameAndProceed} />}
        {step === 2 && <BirthDetailsScreen user={user} onNext={() => setStep(3)} />}
        {step === 3 && <ChooseJourneyScreen user={user} onComplete={onComplete} />}
      </div>
    </div>
  );
}
